import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";
import { Monogram } from "../components/Monogram";
import { OPERATORS } from "../data/operators";

const Team = () => {
    return (
        <main data-testid="page-team" className="text-white">
            {/* Hero */}
            <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-28 overflow-hidden">
                <div className="absolute inset-0 starfield opacity-25 fade-edges pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Link
                            to="/about"
                            data-testid="team-back-link"
                            className="hover-line inline-flex items-center gap-2 text-[12px] tracking-[0.24em] uppercase text-white/55 hover:text-white mb-10"
                        >
                            <ArrowLeft size={14} /> Back to about
                        </Link>
                        <Overline className="mb-8">Team profiles</Overline>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[6rem] leading-[0.98] tracking-tight max-w-5xl">
                            The operators <span className="italic text-accent">behind Orion Road.</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <p className="mt-10 max-w-2xl text-[15px] lg:text-lg leading-relaxed text-white/65">
                            A small, senior team. No headcount growth — just outcome growth. These are
                            the people who plug in, open doors and close business on your behalf.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Profiles */}
            <section className="relative pb-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 divide-y divide-white/[0.06] border-y border-white/[0.06]">
                    {OPERATORS.map((op, i) => (
                        <FadeIn key={op.slug} delay={i * 0.05}>
                            <article
                                id={op.slug}
                                data-testid={`team-profile-${op.slug}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 py-16 lg:py-24"
                            >
                                {/* Index */}
                                <div className="md:col-span-4">
                                    <span className="font-display text-6xl lg:text-7xl text-white/25 leading-none">
                                        0{i + 1}
                                    </span>
                                </div>

                                {/* Bio */}
                                <div className="md:col-span-8 md:pt-2">
                                    <Overline className="mb-4">{op.role}</Overline>
                                    <h2 className="font-display text-5xl lg:text-7xl leading-[1.02] text-white">
                                        {op.name}
                                    </h2>
                                    <p className="mt-8 text-[15px] lg:text-lg leading-relaxed text-white/75 max-w-2xl">
                                        {op.bio}
                                    </p>
                                    {op.quote && (
                                        <blockquote className="mt-10 border-l border-accent pl-6 lg:pl-8 max-w-2xl">
                                            <p className="font-display italic text-2xl lg:text-3xl leading-snug text-white/90">
                                                &ldquo;{op.quote}&rdquo;
                                            </p>
                                            <footer className="mt-4 text-overline">— {op.name}</footer>
                                        </blockquote>
                                    )}
                                </div>
                            </article>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
                    <div className="md:col-span-8">
                        <FadeIn>
                            <Overline className="mb-6">Work with us</Overline>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] text-white">
                                Want a senior operator <span className="italic text-accent">in your corner?</span>
                            </h2>
                        </FadeIn>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            data-testid="team-cta-contact"
                            className="inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                        >
                            Book a conversation <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Team;
